// src/authpages/dashboard/friendsUtils.js
import { 
  db, 
  doc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  getDoc, 
  setDoc,
  collection,
  query,
  where,
  getDocs
} from '../../firebase';

/**
 * Creates a new user document in Firestore with friend-related fields
 * @param {string} userId - The user's Firebase Auth UID
 * @param {Object} userData - The user data to store
 */
export const createUserDocument = async (userId, userData) => {
  try {
    console.log(`Creating user document for user ${userId}`);
    await setDoc(doc(db, "users", userId), {
      // Existing fields
      fullName: userData.fullName,
      email: userData.email.toLowerCase(), // Store email in lowercase for consistent searching
      degreeTitle: userData.degreeTitle,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      
      // Friend-related fields
      friends: [],
      pendingFriendRequests: [],
      sentFriendRequests: [],
      verified: false // Default to false, set to true after email verification
    });
    console.log(`Successfully created user document for ${userId}`);
  } catch (error) {
    console.error(`Error creating user document for ${userId}:`, error);
    throw error;
  }
};

/**
 * Updates a user's verification status in Firestore
 * @param {string} userId - The user's Firebase Auth UID
 */
export const markUserAsVerified = async (userId) => {
  try {
    console.log(`Marking user ${userId} as verified`);
    await updateDoc(doc(db, "users", userId), {
      verified: true
    });
    console.log(`Successfully marked user ${userId} as verified`);
  } catch (error) {
    console.error(`Error marking user ${userId} as verified:`, error);
    throw error;
  }
};

/**
 * Accepts a friend request, updating both users' documents
 * @param {string} currentUserId - The current user's UID
 * @param {string} friendUserId - The friend's UID
 */
export const acceptFriendRequest = async (currentUserId, friendUserId) => {
  try {
    console.log(`User ${currentUserId} accepting friend request from ${friendUserId}`);
    
    // Verify that the request exists in pendingFriendRequests
    const userDoc = await getDoc(doc(db, "users", currentUserId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const pendingRequests = userData.pendingFriendRequests || [];
      
      if (!pendingRequests.includes(friendUserId)) {
        console.warn(`No pending request found from ${friendUserId} for user ${currentUserId}`);
        return false;
      }
    }
    
    // Add to current user's friends list and remove from pending requests
    await updateDoc(doc(db, "users", currentUserId), {
      friends: arrayUnion(friendUserId),
      pendingFriendRequests: arrayRemove(friendUserId)
    });
    
    // Add to friend's friends list and remove from sent requests
    await updateDoc(doc(db, "users", friendUserId), {
      friends: arrayUnion(currentUserId),
      sentFriendRequests: arrayRemove(currentUserId)
    });
    
    console.log(`Successfully accepted friend request from ${friendUserId}`);
    return true;
  } catch (error) {
    console.error(`Error accepting friend request from ${friendUserId}:`, error);
    throw error;
  }
};

/**
 * Sends a friend request from the current user to the target user
 * @param {string} currentUserId - The current user's UID
 * @param {string} targetUserId - The target user's UID
 */
export const sendFriendRequest = async (currentUserId, targetUserId) => {
  try {
    console.log(`User ${currentUserId} sending friend request to ${targetUserId}`);
    
    // Verify target user exists
    const targetDoc = await getDoc(doc(db, "users", targetUserId));
    if (!targetDoc.exists()) {
      console.error(`Target user ${targetUserId} does not exist`);
      return false;
    }
    
    // Check if already friends
    const currentUserDoc = await getDoc(doc(db, "users", currentUserId));
    if (currentUserDoc.exists()) {
      const userData = currentUserDoc.data();
      const friends = userData.friends || [];
      const sentRequests = userData.sentFriendRequests || [];
      
      if (friends.includes(targetUserId)) {
        console.warn(`Users ${currentUserId} and ${targetUserId} are already friends`);
        return false;
      }
      
      if (sentRequests.includes(targetUserId)) {
        console.warn(`User ${currentUserId} has already sent a request to ${targetUserId}`);
        return false;
      }
    }
    
    // Add to current user's sent requests
    await updateDoc(doc(db, "users", currentUserId), {
      sentFriendRequests: arrayUnion(targetUserId)
    });
    
    // Add to target user's pending requests
    await updateDoc(doc(db, "users", targetUserId), {
      pendingFriendRequests: arrayUnion(currentUserId)
    });
    
    console.log(`Successfully sent friend request to ${targetUserId}`);
    return true;
  } catch (error) {
    console.error(`Error sending friend request to ${targetUserId}:`, error);
    throw error;
  }
};

/**
 * Removes a friend connection between two users
 * @param {string} currentUserId - The current user's UID
 * @param {string} friendUserId - The friend's UID
 */
export const removeFriend = async (currentUserId, friendUserId) => {
  try {
    console.log(`User ${currentUserId} removing friend ${friendUserId}`);
    
    // Remove from current user's friends list
    await updateDoc(doc(db, "users", currentUserId), {
      friends: arrayRemove(friendUserId)
    });
    
    // Remove from friend's friends list
    await updateDoc(doc(db, "users", friendUserId), {
      friends: arrayRemove(currentUserId)
    });
    
    console.log(`Successfully removed friend connection between ${currentUserId} and ${friendUserId}`);
    return true;
  } catch (error) {
    console.error(`Error removing friend ${friendUserId}:`, error);
    throw error;
  }
};

/**
 * Rejects a friend request
 * @param {string} currentUserId - The current user's UID
 * @param {string} requesterId - The requester's UID
 */
export const rejectFriendRequest = async (currentUserId, requesterId) => {
  try {
    console.log(`User ${currentUserId} rejecting friend request from ${requesterId}`);
    
    // Remove from current user's pending requests
    await updateDoc(doc(db, "users", currentUserId), {
      pendingFriendRequests: arrayRemove(requesterId)
    });
    
    // Remove from requester's sent requests
    await updateDoc(doc(db, "users", requesterId), {
      sentFriendRequests: arrayRemove(currentUserId)
    });
    
    console.log(`Successfully rejected friend request from ${requesterId}`);
    return true;
  } catch (error) {
    console.error(`Error rejecting friend request from ${requesterId}:`, error);
    throw error;
  }
};

/**
 * Cancels a sent friend request
 * @param {string} currentUserId - The current user's UID
 * @param {string} targetUserId - The target user's UID
 */
export const cancelFriendRequest = async (currentUserId, targetUserId) => {
  try {
    console.log(`User ${currentUserId} canceling friend request to ${targetUserId}`);
    
    // Remove from current user's sent requests
    await updateDoc(doc(db, "users", currentUserId), {
      sentFriendRequests: arrayRemove(targetUserId)
    });
    
    // Remove from target user's pending requests
    await updateDoc(doc(db, "users", targetUserId), {
      pendingFriendRequests: arrayRemove(currentUserId)
    });
    
    console.log(`Successfully canceled friend request to ${targetUserId}`);
    return true;
  } catch (error) {
    console.error(`Error canceling friend request to ${targetUserId}:`, error);
    throw error;
  }
};

/**
 * Gets all friends of a user with their complete profile data
 * @param {string} userId - The user's UID
 * @returns {Array} Array of friend objects with profile data
 */
export const getFriendsWithProfiles = async (userId) => {
  try {
    console.log(`Getting friends with profiles for user ${userId}`);
    const userDoc = await getDoc(doc(db, "users", userId));
    
    if (!userDoc.exists()) {
      console.warn(`User document not found for ${userId}`);
      return [];
    }
    
    const friendIds = userDoc.data().friends || [];
    
    if (friendIds.length === 0) {
      console.log(`User ${userId} has no friends`);
      return [];
    }
    
    const friendsData = await Promise.all(
      friendIds.map(async (friendId) => {
        const friendDoc = await getDoc(doc(db, "users", friendId));
        if (friendDoc.exists()) {
          return {
            id: friendId,
            ...friendDoc.data()
          };
        }
        console.warn(`Friend ${friendId} document not found`);
        return null;
      })
    );
    
    const validFriends = friendsData.filter(Boolean);
    console.log(`Retrieved ${validFriends.length} friends for user ${userId}`);
    return validFriends;
  } catch (error) {
    console.error(`Error getting friends with profiles for ${userId}:`, error);
    return [];
  }
};

/**
 * Searches for users by name or email
 * @param {string} searchQuery - The search query
 * @param {string} currentUserId - The current user's UID (to exclude from results)
 * @returns {Array} Array of matching user objects
 */
export const searchUsers = async (searchQuery, currentUserId) => {
  if (!searchQuery.trim()) {
    return [];
  }
  
  try {
    console.log(`Searching for users with query "${searchQuery}" for user ${currentUserId}`);
    
    // Search for users by email (convert to lowercase for case-insensitive search)
    const emailQuery = query(
      collection(db, "users"),
      where("email", "==", searchQuery.trim().toLowerCase())
    );
    
    // Search for users by name
    const nameQuery = query(
      collection(db, "users"),
      where("fullName", ">=", searchQuery.trim()),
      where("fullName", "<=", searchQuery.trim() + "\uf8ff")
    );
    
    const [emailResults, nameResults] = await Promise.all([
      getDocs(emailQuery),
      getDocs(nameQuery)
    ]);
    
    const results = new Map();
    
    // Process email results
    emailResults.forEach((doc) => {
      if (doc.id !== currentUserId) {
        results.set(doc.id, { id: doc.id, ...doc.data() });
      }
    });
    
    // Process name results
    nameResults.forEach((doc) => {
      if (doc.id !== currentUserId) {
        results.set(doc.id, { id: doc.id, ...doc.data() });
      }
    });
    
    // Convert Map to array
    const searchResultsArray = Array.from(results.values());
    
    // Filter to only show verified users if needed
    // const verifiedResults = searchResultsArray.filter(user => user.verified === true);
    
    // For debugging, let's include all users for now
    console.log(`Found ${searchResultsArray.length} users matching "${searchQuery}"`);
    return searchResultsArray;
  } catch (error) {
    console.error(`Error searching for users with query "${searchQuery}":`, error);
    return [];
  }
};

/**
 * Gets complete user data for a list of user IDs
 * @param {Array} userIds - Array of user IDs
 * @returns {Array} Array of user objects with profile data
 */
export const getUsersWithProfiles = async (userIds) => {
  if (!userIds || userIds.length === 0) {
    return [];
  }
  
  try {
    console.log(`Getting user profiles for ${userIds.length} users`);
    const usersData = await Promise.all(
      userIds.map(async (userId) => {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          return {
            id: userId,
            ...userDoc.data()
          };
        }
        console.warn(`User document for ${userId} not found`);
        return null;
      })
    );
    
    const validUsers = usersData.filter(Boolean);
    console.log(`Retrieved ${validUsers.length} valid user profiles`);
    return validUsers;
  } catch (error) {
    console.error(`Error getting user profiles:`, error);
    return [];
  }
};

/**
 * Fetches all friend-related data for a user (friends, pending requests, sent requests)
 * @param {string} userId - The user's UID
 * @returns {Object} Object containing friends, pendingRequests, and sentRequests arrays
 */
export const fetchAllFriendsData = async (userId) => {
  try {
    console.log(`Fetching all friends data for user ${userId}`);
    // Get user's document with friends list
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      console.warn(`User document for ${userId} not found`);
      return {
        friends: [],
        pendingRequests: [],
        sentRequests: []
      };
    }
    
    const userData = userDoc.data();
    
    // Get lists of IDs
    const friendIds = userData.friends || [];
    const pendingIds = userData.pendingFriendRequests || [];
    const sentIds = userData.sentFriendRequests || [];
    
    console.log(`User ${userId} has:
      - ${friendIds.length} friends
      - ${pendingIds.length} pending requests
      - ${sentIds.length} sent requests`);
    
    // Fetch complete friend data
    const friends = await getUsersWithProfiles(friendIds);
    const pendingRequests = await getUsersWithProfiles(pendingIds);
    const sentRequests = await getUsersWithProfiles(sentIds);
    
    return {
      friends,
      pendingRequests,
      sentRequests
    };
  } catch (error) {
    console.error(`Error fetching all friends data for ${userId}:`, error);
    return {
      friends: [],
      pendingRequests: [],
      sentRequests: []
    };
  }
};